import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import useApplicationUid from '../../hooks/useApplicationUid';
import { selectCustodians } from '../../store/selectors/custodiansSelectors';
import useIraApplicationFundingData from '../../hooks/useApplicationIraFundingData';
import useCustodianInformationDispatch from '../../hooks/useCustodianInformationDispatch';
import { selectCountries } from '../../store/selectors/commonSelectors';
import { getCustodians } from '../../store/actions/custodians/custodiansActions';
import Back from '../Buttons/Back';
import { ModalHeading } from '../Modal';
import Paragraph from '../Paragraph/Paragraph';
import TextButton from '../TextButton/TextButton';
import { FormWrap } from '../FormContainer/FormContainer';
import SearchField from '../Fields/SearchField';
import SelectList from '../SelectList/SelectList';
import { addressHelper } from '../../util/helpers/addressHelper';
import themeColors from '../../assets/styles/themeColors';

const FindCustodianForm = ({
  setIsManual,
  handleNext,
  skipPreviousStep,
  setShouldClearForm,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { ApplicationUid } = useApplicationUid();
  const custodians = useSelector(selectCustodians);
  const { IraApplicationFundingResponse } = useIraApplicationFundingData();
  const { IRAFundingCustodianResponse } = IraApplicationFundingResponse;
  const { custodianInformationDispatch } = useCustodianInformationDispatch();
  const [custodianList, setCustodianList] = useState([]);
  const countries = useSelector(selectCountries);

  const { control, watch } = useForm({
    defaultValues: {
      Custodian: IRAFundingCustodianResponse?.Name || '',
    },
  });

  useEffect(() => {
    dispatch(
      getCustodians({
        ApplicationUid,
      }),
    );
  }, [ApplicationUid, dispatch]);

  const onSubmit = (name) => {
    const selectedCustodian = custodians.find(
      (custodian) => name === custodian.Name,
    );

    custodianInformationDispatch({
      data: {
        AccountNumber: null,
        ...selectedCustodian,
      },
      defaultValues: IRAFundingCustodianResponse,
      onSuccess: handleNext,
    });
  };

  const custodian = watch('Custodian');

  useMemo(() => {
    if (!isEmpty(custodians) && custodian.length > 0) {
      const filteredCustodians = custodians.filter((item) =>
        item?.Name.toUpperCase().includes(custodian.toUpperCase()),
      );

      if (!isEmpty(filteredCustodians)) {
        setCustodianList(filteredCustodians);
        return;
      }

      setCustodianList([]);
    }
  }, [custodians, custodian]);

  useMemo(() => {
    if (custodian.length < 1 && !isEmpty(custodians)) {
      const favorites = custodians.slice(0, 8);

      if (favorites) {
        setCustodianList(favorites);
      }
    }
  }, [custodian, custodians]);

  return (
    <>
      {skipPreviousStep && (
        <Back
          onClick={() => {
            skipPreviousStep();
          }}
        />
      )}
      <ModalHeading>{t('iraCustodian.findCustodian.title')}</ModalHeading>
      <Paragraph mb={20} color={themeColors.colorTextXLLight}>
        {t('iraCustodian.findCustodian.description')}
      </Paragraph>
      <Paragraph mb={24} color={themeColors.colorTextXLLight}>
        {t('iraCustodian.findCustodian.dontSee')}{' '}
        <TextButton
          onClick={() => {
            setIsManual(true);
            setShouldClearForm(false);
          }}
          fontWeight={500}
        >
          {t('iraCustodian.findCustodian.addManually')}
        </TextButton>
      </Paragraph>
      <FormWrap>
        <Controller
          control={control}
          render={({ field, fieldState }) => (
            <SearchField {...field} {...fieldState} autoFocus />
          )}
          name="Custodian"
        />
        {isEmpty(custodianList) ? (
          <Paragraph mt={4}>
            {t('iraCustodian.findCustodian.noCustodian')}
          </Paragraph>
        ) : (
          <SelectList
            mt={4}
            options={custodianList.map((item) => ({
              title: item?.Name,
              description: addressHelper({ Address: item?.Address, countries }),
              selected: item?.Name === IRAFundingCustodianResponse?.Name,
              showDivider: true,
              onClick: () => {
                onSubmit(item?.Name);
              },
            }))}
          />
        )}
      </FormWrap>
    </>
  );
};

FindCustodianForm.propTypes = {
  handleNext: PropTypes.func,
  setIsManual: PropTypes.func,
  skipPreviousStep: PropTypes.func,
  setShouldClearForm: PropTypes.func,
};

export default FindCustodianForm;
